import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { decodeJwt, WINDOW } from '@hglobal/fin-ui';
import dayjs from 'dayjs';
import { Observable, switchMap, tap, throwError } from 'rxjs';

import { environment } from '@kr/environment';
import { LoginType } from '@kr/src/models';
import {
  AccessData,
  AUTH_EXPIRE_KEY,
  AUTH_TOKEN_KEY,
  AuthService,
  AuthStore,
  ExternalRefreshResponse,
  REFRESH_EXPIRE_KEY,
  REFRESH_TOKEN_KEY,
} from '@kr/store';

interface Expirations {
  authTokenExpiry: number;
  refreshTokenExpiry: number;
}

/**
 * Refresh token interceptor
 */
@Injectable()
export class RefreshTokenInterceptor implements HttpInterceptor {
  readonly #http = inject(HttpClient);
  readonly #store = inject(AuthStore);
  readonly #auth = inject(AuthService);
  readonly #window = inject<Window>(WINDOW);

  /**
   * Extra refresh buffer time to be sure that request won't go to the server where token is
   * already expired
   * @private
   */
  #refreshBufferTime = 10000;

  /**
   * Intercept HTTP requests to handle token refresh logic
   * @param request - The outgoing HTTP request
   * @param next - The next handler in the chain
   * @returns An observable of the HTTP event
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const ignoreList = ['auth', 'assets/i18n', 'logout'];
    const ignore = ignoreList.some(item => request.url.includes(item));

    if (environment.useMocks || ignore) {
      return next.handle(request);
    } else {
      const expirations = this.#getExpirations();
      if (expirations) {
        const now = this.#getCurrentTime();

        if (this.#isTokenExpired(expirations.refreshTokenExpiry, now)) {
          this.#auth.logout();
          return throwError(() => new Error('Refresh token is expired'));
        } else if (this.#isTokenExpired(expirations.authTokenExpiry, now)) {
          return this.#refreshAuthToken(request, next);
        } else {
          return next.handle(request);
        }
      } else {
        return next.handle(request);
      }
    }
  }

  /**
   * Get current time in milliseconds
   * @private
   */
  #getCurrentTime(): number {
    return dayjs().valueOf();
  }

  /**
   * Decode JWT token
   * @private
   */
  #decodeToken(token: string): any {
    try {
      return decodeJwt(token, this.#window);
    } catch (e) {
      this.#auth.logout();
      throw new Error('Token is incorrect');
    }
  }

  /**
   * Check if token is expired
   * @private
   */
  #isTokenExpired(expiry: number, now: number): boolean {
    return expiry < now + this.#refreshBufferTime;
  }

  /**
   * Refresh auth token
   * @private
   */
  #refreshAuthToken(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const refresh$ = environment.loginType === LoginType.Internal ? this.#refreshAuthInternalToken() : this.#refreshAuthExternalToken();
    return (refresh$ as Observable<unknown>).pipe(switchMap(() => next.handle(request)));
  }

  /**
   * Refresh auth internal token
   * @private
   */
  #refreshAuthInternalToken(): Observable<Pick<AccessData, 'access' | 'permissions'>> {
    const refreshToken = this.#window.localStorage.getItem(REFRESH_TOKEN_KEY);

    return this.#http
      .post<Pick<AccessData, 'access' | 'permissions'>>(environment.apiHost + `/auth/refresh/`, {
        refresh: refreshToken,
      })
      .pipe(tap(response => this.#store.updateAccessData({ ...response })));
  }

  /**
   * Refresh auth external token
   * @private
   */
  #refreshAuthExternalToken(): Observable<ExternalRefreshResponse> {
    return this.#http.post<ExternalRefreshResponse>(`${environment.apiHost}/external_kc/auth/refresh/`, null).pipe(
      tap(res => {
        this.#store.setAuthStatus(true);
        localStorage.setItem(AUTH_EXPIRE_KEY, res.access_expire.toString());
        localStorage.setItem(REFRESH_EXPIRE_KEY, res.refresh_expire.toString());
      }),
    );
  }

  /**
   * Get access and refresh expiry
   * @private
   */
  #getExpirations(): Expirations {
    return environment.loginType === LoginType.Internal ? this.#getInternalExpiry() : this.#getExternalExpiry();
  }

  /**
   * Get internal access and refresh expiry
   * @private
   */
  #getInternalExpiry(): Expirations {
    const authToken = this.#window.localStorage.getItem(AUTH_TOKEN_KEY);
    const refreshToken = this.#window.localStorage.getItem(REFRESH_TOKEN_KEY);

    return this.#validateTokens(authToken, refreshToken);
  }

  /**
   * Get external access and refresh expiry
   * @private
   */
  #getExternalExpiry(): Expirations {
    const authExpStr = +this.#window.localStorage.getItem(AUTH_EXPIRE_KEY);
    const refreshExpStr = +this.#window.localStorage.getItem(REFRESH_EXPIRE_KEY);
    const authTokenExpiry = dayjs.unix(+authExpStr).valueOf();
    const refreshTokenExpiry = dayjs.unix(+refreshExpStr).valueOf();

    return authTokenExpiry && refreshTokenExpiry ? { authTokenExpiry, refreshTokenExpiry } : null;
  }

  /**
   * Validate tokens and determine if refresh is needed
   * @private
   */
  #validateTokens(authToken: string, refreshToken: string): Expirations {
    if (!authToken || !refreshToken) {
      return null;
    }
    const authData = this.#decodeToken(authToken);
    const refreshData = this.#decodeToken(refreshToken);

    const authTokenExpiry = dayjs.unix((<any>authData).exp).valueOf();
    const refreshTokenExpiry = dayjs.unix((<any>refreshData).exp).valueOf();

    return { authTokenExpiry, refreshTokenExpiry };
  }
}
